import { useMemo } from "react";
import SelectDropdown, {
  type SelectOption,
} from "@/components/ui/select-dropdown";
import { AccentBar } from "@/components/ui/accent-bar/AccentBar";
import type { MissionTemplateSummary } from "@/services/api/agentic-v2";
import type { MissionsFilters } from "./missionsFilters";
import { getMissionsStatusFilterAccentBarBgClass } from "./missionStatusDisplay";

/** Panel at least as wide as the trigger, grows for long labels (see SelectDropdown `dropdownWidth`). */
const FILTER_DROPDOWN_WIDTH = "min-w-full w-max max-w-md";

/**
 * Inline filter bar: Type / Assigned / Status using {@link SelectDropdown}
 * (same pattern as Meetings).
 */
export interface MissionsFilterBarProps {
  filters: MissionsFilters;
  templates: MissionTemplateSummary[];
  onChange: (next: MissionsFilters) => void;
  /** Hide Assigned when Mission Control toolbar owns caseload scope. */
  hideAssignedFilter?: boolean;
  showType?: boolean;
  showStatus?: boolean;
}

function statusAccentBg(
  v: MissionsFilters["statusFilter"],
): string {
  return getMissionsStatusFilterAccentBarBgClass(v);
}

export function MissionsFilterBar({
  filters,
  templates,
  onChange,
  hideAssignedFilter = false,
  showType = true,
  showStatus = true,
}: MissionsFilterBarProps) {
  const typeOptions: SelectOption<string>[] = useMemo(
    () => [
      { value: "all", label: "All types" },
      ...templates.map((t) => ({ value: t._id, label: t.name })),
    ],
    [templates]
  );

  const assignedOptions: SelectOption<MissionsFilters["assigned"]>[] = [
    { value: "me", label: "Me" },
    { value: "team", label: "My team" },
    { value: "everyone", label: "Everyone" },
  ];

  const statusOptions: SelectOption<MissionsFilters["statusFilter"]>[] = [
    { value: "needs_attention", label: "Needs attention" },
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
    { value: "all", label: "All" },
  ];

  return (
    <div
      className="flex flex-wrap items-center gap-6 text-sm"
      data-testid="missions-filter-bar"
    >
      {showType ? (
        <SelectDropdown
          label="Type"
          options={typeOptions}
          value={filters.templateId}
          onChange={(templateId) => onChange({ ...filters, templateId })}
          inline
          className="min-w-0"
          buttonClassName="!cursor-pointer"
          dropdownWidth={FILTER_DROPDOWN_WIDTH}
          data-testid="filter-type-trigger"
          renderListItem={(opt) => (
            <div
              className="py-2 pr-10 pl-3"
              data-testid={`filter-option-${opt.value}`}
            >
              <span className="block whitespace-nowrap font-normal">{opt.label}</span>
            </div>
          )}
        />
      ) : null}
      {!hideAssignedFilter ? (
        <SelectDropdown
          label="Assigned"
          options={assignedOptions}
          value={filters.assigned}
          onChange={(assigned) => onChange({ ...filters, assigned })}
          inline
          className="min-w-0"
          buttonClassName="!cursor-pointer"
          dropdownWidth={FILTER_DROPDOWN_WIDTH}
          data-testid="filter-assigned-trigger"
          renderListItem={(opt) => (
            <div
              className="py-2 pr-10 pl-3"
              data-testid={`filter-option-${opt.value}`}
            >
              <span className="block whitespace-nowrap font-normal">{opt.label}</span>
            </div>
          )}
        />
      ) : null}
      {showStatus ? (
        <SelectDropdown
          label="Status"
          options={statusOptions}
          value={filters.statusFilter}
          onChange={(statusFilter) => onChange({ ...filters, statusFilter })}
          inline
          className="min-w-0"
          buttonClassName="!cursor-pointer"
          dropdownWidth={FILTER_DROPDOWN_WIDTH}
          data-testid="filter-status-trigger"
          renderSelectedItem={(opt) => (
            <span className="flex items-center gap-2 truncate font-normal">
              <AccentBar
                bgClassName={statusAccentBg(opt.value)}
                size="legend"
              />
              {opt.label}
            </span>
          )}
          renderListItem={(opt) => (
            <div
              className="flex w-full items-center gap-2 py-2 pr-10 pl-3"
              data-testid={`filter-option-${opt.value}`}
            >
              <AccentBar
                bgClassName={statusAccentBg(opt.value)}
                size="legend"
              />
              <span className="whitespace-nowrap font-normal">{opt.label}</span>
            </div>
          )}
        />
      ) : null}
    </div>
  );
}
